/**
 * MobileProductsView — the redesigned mobile Products screen (Trioty Products
 * Mobile). Sibling of MobileOrdersView.
 *
 * Self-contained: owns the All/In-stock/Low/Out tabs, the persisted Display
 * prefs (group / sort / row-detail fields) and the category Filter state, and
 * fetches via useProductsQuery. Tapping a row navigates to the product editor;
 * tapping a row opens the full-screen detail view (share · show/hide · edit stock ·
 * delete) and bulk multi-select — nothing from the old mobile screen is lost.
 *
 * The primary "New product" action is real (opens the page's create drawer via
 * `onNewProduct`); Search and Select-products are real too. The ⋯ menu also
 * lists catalog features with no backing endpoint (Import catalog, Export CSV,
 * Sales channels), marked with a gold "not supported" dot for review.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { imageUrl } from "@a2z/ui/media/image-url";
import { MEDIA_BASE_URL } from "@/lib/media-base";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatCurrency } from "@/lib/formatting/currency";
import type { CategoryView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import { useProductsQuery } from "@/modules/products/services/useProductsQuery";
import { useCategoriesQuery } from "@/shared/services/useCategoriesQuery";
import { ProductSelectCheckbox } from "@/modules/products/components/ProductSelectCheckbox";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";
import { CategoryTile } from "@/modules/categories/components/category-ui";
import { categoryColorIndex, categoryTile } from "@/modules/categories/lib/category-display";
import {
  EMPTY_PRODUCTS_FILTERS,
  PRODUCTS_TABS,
  TAB_LABEL,
  deriveProductStatus,
  getPurchasableStockTotal,
  hasUnlimitedStock,
  inTab,
  sortComparator,
  type ProductCardStatus,
  type ProductItem,
  type ProductsFilterState,
  type ProductsGroupBy,
  type ProductsTab,
} from "./products-mobile-model";
import { useProductsDisplayPrefs, type RowProps } from "./useProductsDisplayPrefs";
import { ProductsDisplaySheet } from "./ProductsDisplaySheet";
import { ProductsFilterSheet } from "./ProductsFilterSheet";

type Props = {
  storeSlug: string;
  locale: Locale;
  /** Store-level low-stock threshold (a product's own override still wins). */
  storeLowStock?: number;
  /** Tapping a row opens the full-screen product detail (owned by ProductsPage). */
  onOpenProduct: (id: string) => void;
  /** Opens the existing product create drawer (owned by ProductsPage). */
  onNewProduct: () => void;
  /** Bulk multi-select state, lifted to the page for ProductsBulkCartBar. */
  selectMode: boolean;
  onToggleSelectMode: () => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  /** Reports the loaded page up so the page's bulk cart bar can resolve
   *  selected product objects (the view owns the fetch). */
  onLoadedItemsChange: (items: ProductItem[]) => void;
};

const STATUS_DOT: Record<ProductCardStatus, string> = {
  active: "var(--success-500)",
  low_stock: "var(--warning-500)",
  out_of_stock: "var(--error-500)",
  hidden: "var(--text-disabled)",
};

function parseMoney(value: string | null): number | null {
  if (typeof value !== "string") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getProductThumbnail(product: ProductItem): string | null {
  const readyImage = product.images.find((img) => img.status === "READY");
  if (!readyImage?.objectKey) return null;
  return imageUrl(
    readyImage.objectKey,
    { width: 120, format: "auto", removeBackground: readyImage.useBgRemoved === true },
    { baseUrl: MEDIA_BASE_URL },
  );
}

type BuiltGroup = {
  key: string;
  /** Resolved header text (a category name, or the Uncategorized label). */
  label: string;
  showHeader: boolean;
  rows: ProductItem[];
};

/** Groups the pool by primary category (backend sort order, Uncategorized last)
 *  or returns a single ungrouped list. */
function buildGroups(
  pool: ProductItem[],
  group: ProductsGroupBy,
  categories: CategoryView[],
  locale: Locale,
  uncategorizedLabel: string,
): BuiltGroup[] {
  if (group === "none") {
    return pool.length ? [{ key: "none", label: "", showHeader: false, rows: pool }] : [];
  }
  const known = new Set(categories.map((c) => c.id));
  const buckets = new Map<string, ProductItem[]>();
  for (const p of pool) {
    const catId = p.categoryIds.find((id) => known.has(id)) ?? "__uncat__";
    const arr = buckets.get(catId);
    if (arr) arr.push(p);
    else buckets.set(catId, [p]);
  }
  const ordered = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
  const out: BuiltGroup[] = [];
  for (const c of ordered) {
    const rows = buckets.get(c.id);
    if (rows?.length) {
      out.push({
        key: c.id,
        label: pickLocalized(locale, c.nameAr, c.nameEn),
        showHeader: true,
        rows,
      });
    }
  }
  const uncat = buckets.get("__uncat__");
  if (uncat?.length) {
    out.push({ key: "__uncat__", label: uncategorizedLabel, showHeader: true, rows: uncat });
  }
  return out;
}

export function MobileProductsView({
  storeSlug,
  locale,
  storeLowStock,
  onOpenProduct,
  onNewProduct,
  selectMode,
  onToggleSelectMode,
  selectedIds,
  onToggleSelect,
  onLoadedItemsChange,
}: Props) {
  const t = useTranslations();
  const uiLocale = useLocale();
  const { prefs, setGroup, setSort, toggleProp, resetProps } = useProductsDisplayPrefs(storeSlug);

  const [tab, setTab] = useState<ProductsTab>("all");
  const [filters, setFilters] = useState<ProductsFilterState>(EMPTY_PRODUCTS_FILTERS);
  const [limit, setLimit] = useState(60);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [displayOpen, setDisplayOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Changing the tab or filters is a fresh result set — reset the page size.
  const changeTab = useCallback((next: ProductsTab) => {
    setTab(next);
    setLimit(60);
  }, []);
  const changeFilters = useCallback((next: ProductsFilterState) => {
    setFilters(next);
    setLimit(60);
  }, []);

  const { data: categoriesData } = useCategoriesQuery({ storeSlug });
  const categories = useMemo(() => categoriesData ?? [], [categoriesData]);

  const { data, isLoading, error } = useProductsQuery({
    storeSlug,
    limit,
    categoryIds: filters.category.length ? filters.category : undefined,
  });

  const allProducts = useMemo(() => data?.items ?? [], [data?.items]);
  // Backend has more rows than we've loaded → offer "Load more".
  const canLoadMore = allProducts.length < (data?.total ?? 0);

  // Keep the page's bulk cart bar in sync with the loaded page.
  useEffect(() => {
    onLoadedItemsChange(allProducts);
  }, [allProducts, onLoadedItemsChange]);

  // Derive status once, filter to the active tab, then sort the loaded page.
  const filtered = useMemo(() => {
    const pool = allProducts.filter((p) =>
      inTab(deriveProductStatus(p, storeLowStock), tab),
    );
    return pool.slice().sort(sortComparator(prefs.sort));
  }, [allProducts, tab, storeLowStock, prefs.sort]);

  const groups = useMemo(
    () => buildGroups(filtered, prefs.group, categories, uiLocale, t(keys.products.mUncategorized)),
    [filtered, prefs.group, categories, uiLocale, t],
  );
  const shown = filtered.length;

  const hasCategoryFilter = filters.category.length > 0;

  return (
    <div className="flex flex-col">
      {/* Sticky top region: title + actions + tabs pin while the list scrolls. */}
      <div
        className="sticky top-0 z-20 -mx-4 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
      >
        {/* Header row: title + New product + More */}
        <div className="flex items-center justify-between gap-2 px-1 pb-1 pt-1">
          <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
            {t(keys.seller.productsTitle)}
          </h1>
          <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
            <button
              type="button"
              onClick={onNewProduct}
              aria-label={t(keys.seller.addProductAction)}
              className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
            >
              <PlusIcon />
            </button>
            <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
            <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
            <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
            <Menu as="div" className="relative flex">
              <MenuButton
                aria-label={t(keys.orders.mMore)}
                className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <DotsIcon />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-50 mt-1 w-[210px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
              >
                <ActionMenuItem icon={<SlidersIcon />} label={t(keys.orders.mDisplay)} onClick={() => setDisplayOpen(true)} />
                <ActionMenuItem
                  icon={<SelectIcon />}
                  label={t(selectMode ? keys.products.bulkSelectExit : keys.products.bulkSelectToggle)}
                  onClick={onToggleSelectMode}
                />
              </MenuItems>
            </Menu>
          </div>
        </div>

        {/* Tabs + filter button */}
        <div className="flex items-center gap-1.5 px-1 pb-2 pt-1">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {PRODUCTS_TABS.map((tk) => {
              const on = tk === tab;
              return (
                <button
                  key={tk}
                  type="button"
                  onClick={() => changeTab(tk)}
                  className="whitespace-nowrap rounded-full px-3 py-[5px] text-[13px] font-semibold transition-colors"
                  style={{
                    background: on ? "var(--soft2)" : "transparent",
                    color: on ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  {t(TAB_LABEL[tk])}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            aria-label={t(keys.products.mFilterTitle)}
            className="relative flex h-[34px] w-[34px] items-center justify-center rounded-[10px] transition-colors hover:bg-[color:var(--soft)]"
            style={{ color: hasCategoryFilter ? "var(--accent-500)" : "var(--text-muted)" }}
          >
            <FilterIcon />
            {hasCategoryFilter ? (
              <span
                className="absolute -end-0.5 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1 text-[9.5px] font-bold"
                style={{ background: "var(--accent-500)", color: "var(--on-accent)" }}
              >
                {filters.category.length}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.common.loading)}
        </div>
      ) : error ? (
        <div className="px-1 py-10 text-center text-[13px] text-[color:var(--text-muted)]">
          {t(keys.errors.generic)}
        </div>
      ) : shown === 0 ? (
        <EmptyState
          hasFilters={hasCategoryFilter}
          onClear={() => changeFilters(EMPTY_PRODUCTS_FILTERS)}
          onNewProduct={onNewProduct}
          t={t}
        />
      ) : (
        <div className="pt-1">
          {groups.map((g, gi) => {
            const isCollapsed = !!collapsed[g.key];
            return (
              <div
                key={g.key}
                className={gi === 0 ? "" : "border-t border-[color:var(--hair)]"}
              >
                {g.showHeader ? (
                  <button
                    type="button"
                    onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                    className="flex w-full items-center gap-2.5 px-1 py-3 text-start transition-colors hover:bg-[color:var(--soft)]"
                  >
                    <ChevronIcon rotated={isCollapsed} />
                    {(() => {
                      const tile = categoryTile(categoryColorIndex(g.key));
                      return <CategoryTile bg={tile.bg} fg={tile.fg} size={22} radius={7} iconSize={13} />;
                    })()}
                    <span className="text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
                      {g.label}
                    </span>
                    <span className="text-[12.5px] font-semibold text-[color:var(--text-muted)]">
                      {g.rows.length}
                    </span>
                  </button>
                ) : null}
                {!isCollapsed
                  ? g.rows.map((p, i) => (
                      <ProductRow
                        key={p.id}
                        product={p}
                        locale={locale}
                        propsShown={prefs.props}
                        storeLowStock={storeLowStock}
                        catName={categoryNameOf(p, categories, uiLocale)}
                        onOpenProduct={onOpenProduct}
                        selectMode={selectMode}
                        selected={selectedIds.has(p.id)}
                        onToggleSelect={onToggleSelect}
                        last={i === g.rows.length - 1}
                        t={t}
                      />
                    ))
                  : null}
              </div>
            );
          })}
          {canLoadMore ? (
            <div className="flex justify-center px-1 pb-2 pt-5">
              <button
                type="button"
                onClick={() => setLimit((l) => l + 60)}
                className="inline-flex items-center gap-2 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-opacity hover:opacity-90"
              >
                {t(keys.orders.loadMore)}
              </button>
            </div>
          ) : null}
          <div className="px-1 pb-2 pt-3 text-center text-[11.5px] font-medium text-[color:var(--text-disabled)]">
            {t(keys.products.mProductsCount, { count: shown })}
          </div>
        </div>
      )}

      <ProductsDisplaySheet
        isOpen={displayOpen}
        onClose={() => setDisplayOpen(false)}
        prefs={prefs}
        onGroup={setGroup}
        onSort={setSort}
        onToggleProp={toggleProp}
        onResetProps={resetProps}
      />
      <ProductsFilterSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        categories={categories}
        value={filters}
        onChange={changeFilters}
      />
    </div>
  );
}

/** Primary category name for the row meta (first known category id). */
function categoryNameOf(
  product: ProductItem,
  categories: CategoryView[],
  locale: Locale,
): string | null {
  for (const id of product.categoryIds) {
    const c = categories.find((x) => x.id === id);
    if (c) return pickLocalized(locale, c.nameAr, c.nameEn);
  }
  return null;
}

function ProductRow({
  product,
  locale,
  propsShown,
  storeLowStock,
  catName,
  onOpenProduct,
  selectMode,
  selected,
  onToggleSelect,
  last,
  t,
}: {
  product: ProductItem;
  locale: Locale;
  propsShown: RowProps;
  storeLowStock?: number;
  catName: string | null;
  onOpenProduct: (id: string) => void;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  last: boolean;
  t: ReturnType<typeof useTranslations>;
}) {
  const status = deriveProductStatus(product, storeLowStock);
  const thumb = getProductThumbnail(product);
  const name = pickLocalized(locale, product.nameAr, product.nameEn);

  // Pricing — mirror ProductsPage's list-row derivation.
  const defaultVariant =
    product.variants.find((v) => v.id === product.defaultVariantId) ??
    product.variants.find((v) => v.isDefault) ??
    product.variants[0];
  const fallbackCurrent = defaultVariant
    ? (defaultVariant.discountedPrice ?? defaultVariant.price)
    : 0;
  const fallbackOriginal = defaultVariant ? defaultVariant.price : null;
  const currentMin = parseMoney(product.pricingSummary.currentMin) ?? fallbackCurrent;
  const originalMin = parseMoney(product.pricingSummary.originalMin) ?? fallbackOriginal;
  const hasDiscount = originalMin !== null && originalMin > currentMin;
  const discountPct =
    hasDiscount && originalMin ? Math.round((1 - currentMin / originalMin) * 100) : 0;

  const stockTotal = getPurchasableStockTotal(product);

  // Meta line — toggleable parts joined by " · ".
  const parts: { text: string; color: string }[] = [];
  if (propsShown.category && catName) {
    parts.push({ text: catName, color: "var(--text-muted)" });
  }
  if (propsShown.stock) {
    if (hasUnlimitedStock(product)) {
      parts.push({
        text: t(keys.products.editSidebarInventoryUnlimited),
        color: "var(--text-muted)",
      });
    } else if (stockTotal === 0) {
      parts.push({ text: t(keys.products.statusOutOfStock), color: "var(--error-500)" });
    } else if (status === "low_stock") {
      parts.push({
        text: t(keys.products.mStockLeft, { count: stockTotal }),
        color: "var(--warning-500)",
      });
    } else {
      parts.push({
        text: t(keys.products.mobileInStock, { count: stockTotal }),
        color: "var(--text-muted)",
      });
    }
  }

  const inner = (
    <>
      {/* Thumb + status dot */}
      <div className="relative shrink-0">
        <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-[12px] bg-[color:var(--soft2)] text-[color:var(--text-muted)]">
          {thumb ? (
            <img src={thumb} alt="" aria-hidden="true" className="h-full w-full object-contain" />
          ) : (
            <BagIcon />
          )}
        </div>
        <span
          aria-hidden="true"
          className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-[2.5px] border-[color:var(--canvas)]"
          style={{ background: STATUS_DOT[status] }}
        />
      </div>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
          {name}
        </div>
        {parts.length ? (
          <div className="mt-0.5 truncate text-[12px]">
            {parts.map((p, i) => (
              <span key={i}>
                {i > 0 ? <span className="text-[color:var(--text-disabled)]"> · </span> : null}
                <span style={{ color: p.color }}>{p.text}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* Right: discount chip + price + old price */}
      <div className="flex shrink-0 items-center gap-2">
        {hasDiscount && discountPct > 0 ? (
          <span
            className="rounded-[6px] px-1.5 py-0.5 text-[10.5px] font-extrabold"
            style={{
              background: "color-mix(in srgb, var(--warning-500) 15%, transparent)",
              color: "var(--warning-500)",
            }}
          >
            {t(keys.products.mDiscountShort, { pct: discountPct })}
          </span>
        ) : null}
        <div className="text-end">
          <div className="text-[15px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
            <bdi>{formatCurrency(currentMin, locale)}</bdi>
          </div>
          {hasDiscount && originalMin ? (
            <div className="mt-px text-[11.5px] text-[color:var(--text-muted)] line-through">
              <bdi>{formatCurrency(originalMin, locale)}</bdi>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <div
      className="relative flex items-center gap-1.5 px-1 transition-colors hover:bg-[color:var(--soft)]"
      style={{ opacity: status === "out_of_stock" ? 0.62 : 1 }}
    >
      {selectMode ? (
        <ProductSelectCheckbox
          checked={selected}
          onToggle={() => onToggleSelect(product.id)}
          ariaLabel={t(keys.products.bulkSelectAria)}
        />
      ) : null}

      {selectMode ? (
        <button
          type="button"
          onClick={() => onToggleSelect(product.id)}
          aria-pressed={selected}
          className="flex min-w-0 flex-1 items-center gap-3 py-3 text-start"
        >
          {inner}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onOpenProduct(product.id)}
          className="flex min-w-0 flex-1 items-center gap-3 py-3 text-start"
        >
          {inner}
        </button>
      )}

      {!last ? (
        <span className="absolute bottom-0 end-0 start-[57px] h-px bg-[color:var(--hair)]" />
      ) : null}
    </div>
  );
}

function ActionMenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <MenuItem>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--soft)]"
      >
        <span className="text-[color:var(--text-muted)]">{icon}</span>
        {label}
      </button>
    </MenuItem>
  );
}

function EmptyState({
  hasFilters,
  onClear,
  onNewProduct,
  t,
}: {
  hasFilters: boolean;
  onClear: () => void;
  onNewProduct: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-10 py-16 text-center">
      <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[15px] bg-[color:var(--soft)] text-[color:var(--text-muted)]">
        <BagIcon size={24} />
      </div>
      <div className="text-[15px] font-semibold tracking-[-0.01em] text-[color:var(--text-secondary)]">
        {t(hasFilters ? keys.products.filteredEmptyTitle : keys.common.emptyTitle)}
      </div>
      <div className="mt-1.5 max-w-[230px] text-[13px] leading-relaxed text-[color:var(--text-muted)]">
        {t(hasFilters ? keys.products.filteredEmptyHint : keys.seller.emptyProductsHint)}
      </div>
      {hasFilters ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-4 rounded-[11px] bg-[color:var(--soft2)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--text-primary)]"
        >
          {t(keys.common.clearFilters)}
        </button>
      ) : (
        <button
          type="button"
          onClick={onNewProduct}
          className="mt-4 rounded-[11px] bg-[color:var(--accent-500)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--on-accent)]"
        >
          {t(keys.seller.addProductAction)}
        </button>
      )}
    </div>
  );
}

// ─── icons ───────────────────────────────────────────────────────────────────

const ICON = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function PlusIcon() {
  return (
    <svg {...ICON} strokeWidth={2.2}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...ICON} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}
function SlidersIcon() {
  return (
    <svg {...ICON}>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="13" cy="18" r="2" />
    </svg>
  );
}
function SelectIcon() {
  return (
    <svg {...ICON}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg {...ICON} strokeWidth={1.9}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
function ChevronIcon({ rotated }: { rotated: boolean }) {
  return (
    <svg
      {...ICON}
      width={11}
      height={11}
      strokeWidth={3}
      className="shrink-0 text-[color:var(--text-muted)] transition-transform"
      style={{ transform: rotated ? "rotate(-90deg)" : "rotate(0deg)" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
/** Small tag chip used in the category group header (not a status ring). */
function BagIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 8V6a4 4 0 0 1 8 0v2" />
      <rect x="5" y="8" width="14" height="13" rx="2.5" />
    </svg>
  );
}
